<script setup>
import UpdateTransportDialog from '@/components/transport-summary-page/UpdateTransportDialog.vue'
import UpdateInvoiceDialog from '@/components/transport-summary-page/UpdateInvoiceDialog.vue'
import RevenueFilter from '@/components/revenue-page/RevenueFilter.vue'
import BaseAlert from '@/components/base/BaseAlert.vue'
import PaginationBase from '../components/PaginationBase.vue'
import RevenueItemCard from '@/components/revenue-page/RevenueItemCard.vue'
import RevenueSummaryCard from '@/components/revenue-page/RevenueSummaryCard.vue'
import UpdateInvoiceStatusDialog from '@/components/revenue-page/UpdateInvoiceStatusDialog.vue'
import UpdateRevenueCustomerDialog from '@/components/revenue-page/UpdateRevenueCustomerDialog.vue'
import ExportValidationAlert from '@/components/revenue-page/ExportValidationAlert.vue'

import { ref, computed, onMounted } from 'vue'
import { useStore } from '@/utils/VuexHelpers'
import { CARTON } from '@/constants/general'
import { CUSTOMERS } from '@/constants/pagination.js'
import { exportXeroInvoiceCsv } from '@/utils/xero/exportXeroInvoiceCsv'
import { INVOICE_STATUS } from '@/constants/invoiceStatus.js'

const store = useStore()

const EXPORT_ERROR_TYPES = {
  VALIDATION: 'validation',
  LOCK_FAILED: 'lock_failed',
  NO_DOCKETS: 'no_dockets'
}

const transportDialog = ref(null)
const invoiceDialog = ref(null)
const invoiceStatusDialog = ref(null)
const customerDialog = ref(null)

const cartonHeaders = [
  { value: 'productname', text: 'Product Name', sortable: true },
  { value: 'productcode', text: 'Product Code', sortable: true },
  { value: 'producttype', text: 'Product Type', sortable: true },
  { value: 'weight', text: 'Weight (kg)', sortable: true },
  { value: 'price_kg', text: 'Price/Kg', sortable: true },
  { value: 'cartons', text: 'Cartons', sortable: false },
  { value: 'line_total', text: 'Line Total', sortable: true }
]

const porkerHeaders = [
  { value: 'body_no', text: 'Body Number', sortable: true },
  { value: 'producttype', text: 'Product Type', sortable: true },
  { value: 'weight', text: 'Weight (kg)', sortable: true },
  { value: 'price_kg', text: 'Price/Kg', sortable: true },
  { value: 'line_total', text: 'Line Total', sortable: true }
]

const revenueList = computed(() => store.getters.revenueList)
const totalRevenue = computed(() => store.getters.totalRevenue)
const perPageRevenue = computed(() => store.getters.perPageRevenue)
const pageNumberRevenue = computed(() => store.getters.pageNumberRevenue)
const perPageOptions = computed(() => store.getters.perPageOptions)
const loadingRevenue = computed(() => store.getters.loadingRevenue)
const freightCodeMap = computed(() => store.getters.freightCodeMap)
const freightNames = computed(() => store.getters.freightNames)
const loadingSuppliers = computed(() => store.getters.loadingSuppliers)

const perPage = computed({
  get: () => perPageRevenue.value,
  set: (value) => {
    store.commit('SET_PER_PAGE_REVENUE', value)
    store.dispatch('fetchRevenue')
  }
})

const isCartonItem = (item) => {
  if (
    item.loaded_items?.length > 0 &&
    item.loaded_items?.[0].producttype === CARTON
  )
    return true
  return false
}

const updateFreight = (payload) => {
  transportDialog.value.edit(payload)
}

const updateInvoice = (payload) => {
  invoiceDialog.value.edit(payload)
}

const updatePageNr = (nr) => {
  store.commit('SET_PAGE_NR_REVENUE', nr - 1)
  store.dispatch('fetchRevenue')
}

const freightCode = (deliveryIds) => {
  if (!deliveryIds) return '-'

  let arr = deliveryIds.split(',')
  const mapped = arr.map((el) => {
    if (el) {
      return freightCodeMap.value?.[el]?.code ?? ''
    }
  })
  return mapped.filter(Boolean).join('/')
}

const exportErrors = ref([])
const exportErrorType = ref(null)

const clearExportErrors = () => {
  exportErrors.value = []
  exportErrorType.value = null
}

const getIdList = (dockets) => {
  return dockets.map((docket) => docket.id).join(',')
}

const exportAccountingCsv = async () => {
  exportErrors.value = []

  const docketsToExport = revenueList.value.filter((docket) => {
    return docket.invoice_status === INVOICE_STATUS.DRAFT
  })

  if (!docketsToExport.length) {
    exportErrorType.value = EXPORT_ERROR_TYPES.NO_DOCKETS
    exportErrors.value = [
      'Please adjust your filters or update docket statuses to proceed.'
    ]
    return
  }

  const result = exportXeroInvoiceCsv(docketsToExport)

  if (!result.success) {
    exportErrorType.value = EXPORT_ERROR_TYPES.VALIDATION
    exportErrors.value = result.errors
    return
  }

  const idList = getIdList(docketsToExport)

  const lockRes = await store.dispatch('generateRevenueInvoices', {
    idList
  })

  if (lockRes === 'error') {
    exportErrorType.value = EXPORT_ERROR_TYPES.LOCK_FAILED
    exportErrors.value = [
      'CSV was exported, but the exported dockets could not be locked as invoiced. Please refresh and check docket statuses.'
    ]
    return
  }

  store.dispatch('fetchRevenue')
}

const updateInvoiceStatus = (item) => {
  invoiceStatusDialog.value.edit(item)
}

const updateCustomer = (item) => {
  customerDialog.value.edit(item)
}

// ON CREATED
store.commit('SET_USING_VUETIFY', true)
store.commit('SET_ACTIVE', CUSTOMERS)

onMounted(() => {
  store.dispatch('fetchRevenue')

  if (
    freightNames.value?.length === 0 &&
    loadingSuppliers.value !== 'freightnames'
  ) {
    store.dispatch('fetchFreightNames')
  }
})
</script>

<template>
  <v-app>
    <BaseAlert />

    <v-container fluid>
      <div class="wrapper">
        <RevenueFilter class="sticky-side-filter" />

        <div class="content-page mt-0">
          <v-row no-gutters class="my-2 align-center">
            <div class="page-title-box d-flex">
              <h4 class="page-title">Revenue</h4>
            </div>
            <v-btn
              class="ms-auto"
              small
              color="success"
              depressed
              :loading="loadingRevenue === 'generate-invoices'"
              @click="exportAccountingCsv"
            >
              <v-icon small left>mdi-file-delimited-outline</v-icon>
              Export Accounting CSV
            </v-btn>
          </v-row>

          <ExportValidationAlert
            :errors="exportErrors"
            :error-type="exportErrorType"
            @clear="clearExportErrors"
          />

          <UpdateTransportDialog
            ref="transportDialog"
            :parent-type="'revenue'"
          />
          <UpdateInvoiceDialog ref="invoiceDialog" :parent-type="'revenue'" />
          <UpdateInvoiceStatusDialog ref="invoiceStatusDialog" />
          <UpdateRevenueCustomerDialog ref="customerDialog" />

          <v-row class="mt-1 mb-3 align-center" no-gutters>
            <div class="d-flex align-center">
              Revenue Summary:
              <span class="font-weight-bold ms-2">{{ totalRevenue }}</span>
            </div>
            <div class="ms-auto me-2 d-flex align-center">
              Summaries per page:
            </div>
            <b-select
              v-model="perPage"
              :options="perPageOptions"
              class="d-inline-block w-auto mr-4"
            ></b-select>
            <PaginationBase
              :total="totalRevenue"
              :perPage="perPageRevenue"
              :current="pageNumberRevenue + 1"
              @updatePage="updatePageNr"
            />
          </v-row>

          <RevenueSummaryCard
            :revenue-list="revenueList"
            :freight-code="freightCode"
            :loading="loadingRevenue === 'list'"
          />

          <RevenueItemCard
            v-for="item in revenueList"
            :key="item.id"
            :item="item"
            :loading="
              loadingRevenue === 'list' ||
              loadingRevenue === `update-prices-${item.id}`
            "
            :carton-headers="cartonHeaders"
            :porker-headers="porkerHeaders"
            :is-carton-item="isCartonItem(item)"
            :freight-code="freightCode"
            @update-freight="updateFreight"
            @update-invoice="updateInvoice"
            @update-customer="() => updateCustomer(item)"
            @update-invoice-status="() => updateInvoiceStatus(item)"
          />
        </div>
      </div>
    </v-container>
  </v-app>
</template>

<style scoped>
p {
  margin-bottom: 0;
  line-height: 1.1;
}

.sticky-side-filter {
  position: sticky;
  top: 12px;
  align-self: flex-start;
  max-height: calc(100vh - 24px);
  overflow-y: auto;
  z-index: 5;
}
</style>
